/**
 * Overview Page (Demand Snapshot)
 * Extracted from App.js - Phase 7C Frontend Refactor
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import DemandMomentumIndicator from "../components/demand/DemandMomentumIndicator";

const OverviewPage = () => {
  const { apiFetch, user } = useAuth();
  const isMasterAdmin = user?.role === "MASTER_ADMIN";
  const toast = useToast();
  const [demandSummary, setDemandSummary] = useState(null);
  const [topVehicles, setTopVehicles] = useState([]);
  const [topSources, setTopSources] = useState([]);
  const [topZips, setTopZips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const errorShownRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [summaryRes, vehiclesRes, marketingRes, geoRes] = await Promise.all([
          apiFetch("/demand/scores/summary", { method: "GET" }),
          apiFetch("/demand/scores/vehicles?limit=3", { method: "GET" }),
          apiFetch("/demand/scores/marketing", { method: "GET" }),
          apiFetch("/demand/scores/geographic?days=7", { method: "GET" }).catch(() => ({ data: { zip_bubbles: [] } }))
        ]);
        if (isMounted) {
          setDemandSummary(summaryRes.data);
          setTopVehicles(vehiclesRes.data.vehicles?.slice(0, 3) || []);
          setTopSources(marketingRes.data.sources?.slice(0, 2) || []);
          setTopZips(geoRes.data.zip_bubbles?.slice(0, 1) || []);
          setIsLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error(e);
          setIsLoading(false);
          if (!errorShownRef.current) {
            errorShownRef.current = true;
            toast.error("Failed to load demand snapshot", "load_snapshot");
          }
        }
      }
    };
    
    fetchData();
    return () => { isMounted = false; };
  }, [apiFetch, toast]);

  const getDemandLevelColor = (level) => {
    switch (level) {
      case "high": return "#10b981";
      case "strong": return "#3b82f6";
      case "emerging": return "#f59e0b";
      case "low": return "#6b7280";
      default: return "#6b7280";
    }
  };

  const getDemandLevelBg = (level) => {
    switch (level) {
      case "high": return "#dcfce7";
      case "strong": return "#dbeafe";
      case "emerging": return "#fef3c7";
      case "low": return "#f3f4f6";
      default: return "#f3f4f6";
    }
  };

  const generateInterpretation = () => {
    if (!demandSummary) return null;
    
    const verified = demandSummary.verified_leads || 0;
    const unverified = demandSummary.unverified_visitors || 0;
    const total = verified + unverified;
    const verifiedPct = total > 0 ? Math.round((verified / total) * 100) : 0;
    const level = demandSummary.overall_demand_level || "low";
    const verifiedHigh = demandSummary.verified_high_demand_count || 0;
    
    if (total === 0) {
      return "No buyer signals detected yet. Demand data will appear as visitors interact with your inventory listings.";
    }
    
    if (level === "low") {
      return "Buyers are browsing but haven't shown strong purchase signals. This could indicate early-stage window shopping or inventory that needs more visibility.";
    }
    
    if (verifiedHigh > 0) {
      return `You have ${verifiedHigh} verified buyer${verifiedHigh > 1 ? 's' : ''} showing high intent. These are your most credible prospects with confirmed contact information.`;
    }
    
    if (verifiedPct < 20) {
      return "Buyers are engaging with inventory and showing interest, but verification rates suggest early-stage exploration rather than immediate purchase readiness.";
    }
    
    if (verifiedPct >= 50) {
      return "Strong demand with high verification rates indicates serious buyer interest. Many visitors have confirmed their identity while researching your inventory.";
    }
    
    return "Demand is building with a mix of verified and anonymous interest. Continued engagement may convert browsers into serious prospects.";
  };

  const formatSourceName = (source) => {
    if (source === "(direct)") return "Direct Traffic";
    if (source === "(none)") return "Unknown";
    return source.charAt(0).toUpperCase() + source.slice(1);
  };

  if (isLoading) return <div className="page-container"><div className="loading-spinner">Loading demand snapshot...</div></div>;

  const hasData = demandSummary && (demandSummary.unique_visitors > 0 || demandSummary.total_events > 0);
  const overallLevel = demandSummary?.overall_demand_level || "low";
  const overallLabel = demandSummary?.overall_demand_label || "Low Demand";
  const verified = demandSummary?.verified_leads || 0;
  const unverified = demandSummary?.unverified_visitors || 0;
  const totalDemand = verified + unverified;
  const verifiedPct = totalDemand > 0 ? Math.round((verified / totalDemand) * 100) : 0;
  const verifiedHigh = demandSummary?.verified_high_demand_count || 0;

  return (
    <div className="page-container demand-snapshot-page" data-testid="overview-page">
      {/* Primary Demand Status - Above the Fold */}
      <div className="demand-hero-section" style={{ backgroundColor: getDemandLevelBg(overallLevel) }}>
        <div className="demand-hero-content">
          <div className="demand-hero-question">Do you have real buyer demand right now?</div>
          <div className="demand-hero-answer">
            <span 
              className="demand-hero-level"
              style={{ color: getDemandLevelColor(overallLevel) }}
            >
              {overallLabel}
            </span>
            {verifiedHigh > 0 && (
              <span className="demand-hero-verified-tag">
                <span className="vt-check">✓</span> {verifiedHigh} Verified Demand
              </span>
            )}
          </div>
          <p className="demand-hero-explanation">
            {demandSummary?.overall_explanation || "No demand signals detected yet."}
          </p>
          
          {/* Confidence Indicator */}
          <div className="confidence-indicator">
            <span className="ci-label">Confidence:</span>
            <div className="ci-bar">
              <div 
                className="ci-fill verified" 
                style={{ width: `${verifiedPct}%` }}
                title={`${verifiedPct}% Verified`}
              />
            </div>
            <span className="ci-text">
              {verifiedPct}% of demand is verified
            </span>
          </div>
        </div>
      </div>

      {/* Verified Demand Share */}
      <div className="snapshot-section">
        <h3 className="snapshot-section-title">Demand Credibility</h3>
        <p className="snapshot-section-subtitle">Demand exists before leads. Verification increases confidence.</p>
        <div className="demand-share-visual">
          <div className="share-block verified-share">
            <div className="share-icon verified">✓</div>
            <div className="share-value">{verified}</div>
            <div className="share-label">Verified Demand</div>
            <div className="share-desc">Confirmed buyer identity</div>
          </div>
          <div className="share-divider">
            <span className="share-plus">+</span>
          </div>
          <div className="share-block anonymous-share">
            <div className="share-icon anonymous">?</div>
            <div className="share-value">{unverified}</div>
            <div className="share-label">Anonymous Intent</div>
            <div className="share-desc">Showing buying signals</div>
          </div>
          <div className="share-divider">
            <span className="share-equals">=</span>
          </div>
          <div className="share-block total-share">
            <div className="share-value total">{totalDemand}</div>
            <div className="share-label">Total Demand Signals</div>
          </div>
        </div>
        {!hasData && (
          <div className="empty-hint">
            Demand signals will appear as visitors interact with your inventory.
          </div>
        )}
      </div>

      {/* Demand Concentration Preview */}
      <div className="snapshot-section">
        <h3 className="snapshot-section-title">Where Demand is Concentrated</h3>
        <div className="concentration-grid">
          {/* Top Vehicles */}
          <div className="concentration-card">
            <div className="conc-header">
              <span className="conc-icon">🚗</span>
              <span className="conc-title">Top Vehicles</span>
            </div>
            {topVehicles.length === 0 ? (
              <div className="conc-empty">Vehicle demand data will appear once visitors interact with your inventory.</div>
            ) : (
              <div className="conc-items">
                {topVehicles.map((v, i) => (
                  <div key={i} className="conc-item">
                    <span className="conc-item-name">
                      {v.vehicle_year} {v.vehicle_make} {v.vehicle_model}
                    </span>
                    <div className="conc-item-badges">
                      {v.has_verified_demand && <span className="mini-verified">✓</span>}
                      <span 
                        className="mini-demand-badge"
                        style={{ 
                          backgroundColor: getDemandLevelBg(v.demand_level),
                          color: getDemandLevelColor(v.demand_level)
                        }}
                      >
                        {v.demand_label?.split(' ')[0]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <a href="#" className="conc-link" onClick={(e) => { e.preventDefault(); }}>View all vehicles →</a>
          </div>

          {/* Top Marketing Sources */}
          <div className="concentration-card">
            <div className="conc-header">
              <span className="conc-icon">📣</span>
              <span className="conc-title">Top Sources</span>
            </div>
            {topSources.length === 0 ? (
              <div className="conc-empty">Source data will appear once visitors arrive at your site.</div>
            ) : (
              <div className="conc-items">
                {topSources.map((s, i) => (
                  <div key={i} className="conc-item">
                    <span className="conc-item-name">{formatSourceName(s.source)}</span>
                    <div className="conc-item-badges">
                      {s.confidence_label === "verified" && <span className="mini-verified">✓</span>}
                      <span 
                        className="mini-demand-badge"
                        style={{ 
                          backgroundColor: getDemandLevelBg(s.demand_level),
                          color: getDemandLevelColor(s.demand_level)
                        }}
                      >
                        {s.demand_label?.split(' ')[0]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <a href="#" className="conc-link" onClick={(e) => { e.preventDefault(); }}>View marketing quality →</a>
          </div>

          {/* Top ZIP Cluster — hidden for master admin (geographic demand is a dealer tool) */}
          {!isMasterAdmin && (
          <div className="concentration-card">
            <div className="conc-header">
              <span className="conc-icon">🗺️</span>
              <span className="conc-title">Top Location</span>
            </div>
            {topZips.length === 0 ? (
              <div className="conc-empty">Geographic data will appear once visitors interact with your inventory.</div>
            ) : (
              <div className="conc-items">
                {topZips.map((z, i) => (
                  <div key={i} className="conc-item">
                    <span className="conc-item-name">
                      {z.city ? `${z.city}, ${z.state}` : `ZIP ${z.zip_code}`}
                    </span>
                    <div className="conc-item-badges">
                      {z.verified_high_demand_count > 0 && <span className="mini-verified">✓</span>}
                      <span 
                        className="mini-demand-badge"
                        style={{ 
                          backgroundColor: getDemandLevelBg(z.demand_level),
                          color: getDemandLevelColor(z.demand_level)
                        }}
                      >
                        {z.demand_label?.split(' ')[0]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <a href="#" className="conc-link" onClick={(e) => { e.preventDefault(); }}>View geographic demand →</a>
          </div>
          )}
        </div>
      </div>

      {/* Demand Momentum Indicator */}
      <DemandMomentumIndicator />

      {/* What This Means - Interpretation Box */}
      <div className="interpretation-box">
        <div className="interp-header">
          <span className="interp-icon">💡</span>
          <span className="interp-title">What This Means</span>
        </div>
        <p className="interp-text">{generateInterpretation()}</p>
      </div>
    </div>
  );
};

export default OverviewPage;
